<style scoped>

.dashboard_vif_monitoring {
    padding: 24px;
}

.monitoring_layout {
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(300px, 1fr) 72px minmax(300px, 1fr);
}

.monitoring_card {
    min-height: 460px;
}

.monitoring_list {
    max-height: 360px;
    overflow-y: auto;
}

.monitoring_empty {
    color: rgba(0, 0, 0, 0.6);
    padding: 48px 20px;
    text-align: center;
}

.monitoring_transfer_controls {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: center;
}

.br {
    color: rgba(109, 103, 164);
}

@media (max-width: 900px) {
    .monitoring_layout {
        grid-template-columns: 1fr;
    }

    .monitoring_transfer_controls {
        flex-direction: row;
    }
}

</style>

<template>
<div class="dashboard_vif_monitoring">
    <h1 class="br mb-4">Select VIFs for monitoring</h1>

    <v-alert type="info" outlined>
        Only VIFs in the monitored list will be collected. Enter a complete,
        case-sensitive VIF name; BR uses an exact name lookup.
    </v-alert>

    <v-alert
        v-if="message"
        :type="message_type"
        dismissible
        outlined
        @input="message = ''"
    >
        {{ message }}
    </v-alert>

    <div class="monitoring_layout">
        <v-card class="monitoring_card" outlined>
            <v-card-title>Existing VIF</v-card-title>
            <v-card-subtitle>
                Find one VIF by its exact name on BR instance {{ instance }}.
            </v-card-subtitle>
            <v-card-text>
                <v-text-field
                    v-model="search_name"
                    label="Complete VIF name"
                    append-icon="mdi-magnify"
                    :error-messages="search_validation_error"
                    :disabled="searching || list_loading"
                    clearable
                    outlined
                    dense
                    @input="clear_search_result"
                    @click:append="search_vif"
                    @keyup.enter="search_vif"
                ></v-text-field>
                <v-btn
                    color="primary"
                    :disabled="!search_name_is_valid || searching || list_loading"
                    :loading="searching"
                    @click="search_vif"
                >
                    Find VIF
                </v-btn>
            </v-card-text>

            <v-divider></v-divider>

            <v-progress-linear
                v-if="searching"
                indeterminate
                color="primary"
            ></v-progress-linear>

            <v-list
                v-if="search_results.length > 0"
                class="monitoring_list"
            >
                <v-list-item-group
                    v-model="selected_search_index"
                    color="primary"
                >
                    <v-list-item
                        v-for="vif in search_results"
                        :key="vif.name"
                        @dblclick="add_vif_on_double_click(vif)"
                    >
                        <v-list-item-content>
                            <v-list-item-title>{{ vif.name }}</v-list-item-title>
                            <v-list-item-subtitle>
                                ID {{ vif.id }}, port {{ vif.port }},
                                SVID {{ vif.svid }}, CVID {{ vif.cvid }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action v-if="vif.monitored">
                            <v-chip color="primary" small outlined>
                                Monitored
                            </v-chip>
                        </v-list-item-action>
                    </v-list-item>
                </v-list-item-group>
            </v-list>

            <div
                v-else-if="search_attempted && !searching"
                class="monitoring_empty"
            >
                {{ search_message }}
            </div>
            <div v-else-if="!searching" class="monitoring_empty">
                Enter the full name of a VIF to find it.
            </div>
        </v-card>

        <div class="monitoring_transfer_controls">
            <v-btn
                color="primary"
                fab
                small
                title="Add selected VIF"
                aria-label="Add selected VIF"
                :disabled="!can_add"
                :loading="adding"
                @click="add_selected_vif"
            >
                <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn
                color="primary"
                fab
                small
                outlined
                title="Remove selected VIF"
                aria-label="Remove selected VIF"
                :disabled="!can_remove"
                :loading="removing"
                @click="remove_selected_vif"
            >
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
        </div>

        <v-card class="monitoring_card" outlined>
            <v-card-title>Monitored VIFs</v-card-title>
            <v-card-subtitle>
                These VIFs are enabled for metrics collection on BR instance
                {{ instance }}.
            </v-card-subtitle>

            <v-progress-linear
                v-if="list_loading"
                indeterminate
                color="primary"
            ></v-progress-linear>

            <v-list
                v-if="monitored_vifs.length > 0"
                class="monitoring_list"
            >
                <v-list-item-group
                    v-model="selected_monitored_index"
                    color="primary"
                >
                    <v-list-item
                        v-for="vif in monitored_vifs"
                        :key="vif.name"
                        @dblclick="remove_vif_on_double_click(vif)"
                    >
                        <v-list-item-content>
                            <v-list-item-title>{{ vif.name }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list-item-group>
            </v-list>

            <div
                v-else-if="!list_loading"
                class="monitoring_empty"
            >
                No VIFs are currently monitored.
            </div>
        </v-card>
    </div>
</div>
</template>

<script>

import api_client from "../services/api.js";


const VIF_NAME_MAX_LENGTH = 95;


function request_error_message(error, fallback) {
    const response = error.response;
    return response && response.data
        ? response.data.detail || fallback
        : fallback;
}


function parse_vif_response(data) {
    if (typeof data !== "string") {
        return data;
    }
    if (!data) {
        return null;
    }
    try {
        return JSON.parse(data);
    }
    catch {
        return null;
    }
}


export default {
    name: "dashboard_vif_monitoring",

    data: () => ({
        adding: false,
        instance: 1,
        list_loading: false,
        message: "",
        message_type: "success",
        monitored_vifs: [],
        removing: false,
        search_attempted: false,
        search_message: "",
        search_name: "",
        search_results: [],
        searching: false,
        selected_monitored_index: null,
        selected_search_index: null,
    }),

    computed: {
        can_add() {
            return this.selected_search_vif !== null &&
                !this.selected_search_vif.monitored &&
                !this.adding && !this.removing;
        },

        can_remove() {
            return this.selected_monitored_vif !== null &&
                !this.adding && !this.removing;
        },

        search_name_is_valid() {
            const name = (this.search_name || "").trim();
            return name.length > 0 &&
                name.length <= VIF_NAME_MAX_LENGTH &&
                !/\s/.test(name);
        },

        search_validation_error() {
            const name = this.search_name || "";
            if (!name) {
                return "";
            }
            if (/\s/.test(name.trim())) {
                return "A VIF name cannot contain whitespace.";
            }
            if (name.trim().length > VIF_NAME_MAX_LENGTH) {
                return `A VIF name cannot exceed ${VIF_NAME_MAX_LENGTH} characters.`;
            }
            return "";
        },

        selected_monitored_vif() {
            if (this.selected_monitored_index === null ||
                this.selected_monitored_index === undefined) {
                return null;
            }
            return this.monitored_vifs[this.selected_monitored_index] || null;
        },

        selected_search_vif() {
            if (this.selected_search_index === null ||
                this.selected_search_index === undefined) {
                return null;
            }
            return this.search_results[this.selected_search_index] || null;
        },
    },

    created() {
        this.load_monitored_vifs();
    },

    methods: {
        clear_search_result() {
            this.search_attempted = false;
            this.search_message = "";
            this.search_results = [];
            this.selected_search_index = null;
        },

        load_monitored_vifs() {
            this.list_loading = true;
            return api_client.run_get_query('/dashboard/vif_monitoring')
            .then(response => {
                const data = response.data || {};
                const vifs = Array.isArray(data.vifs) ? data.vifs : [];
                this.instance = data.instance || 1;
                this.monitored_vifs = vifs
                    .filter(vif => vif && typeof vif.name === "string")
                    .sort((left, right) => left.name.localeCompare(right.name));
                this.selected_monitored_index = null;
            })
            .catch(error => {
                this.message_type = "error";
                this.message = request_error_message(
                    error,
                    "Could not load monitored VIFs."
                );
            })
            .finally(() => {
                this.list_loading = false;
            });
        },

        search_vif() {
            if (!this.search_name_is_valid || this.searching ||
                this.list_loading) {
                return;
            }

            const name = this.search_name.trim();
            this.searching = true;
            this.search_attempted = true;
            this.search_message = "";
            this.search_results = [];
            this.selected_search_index = null;
            this.message = "";

            api_client.run_post_query('/vif/show_with_name', { name })
            .then(response => {
                const vif = parse_vif_response(response.data);
                if (!vif || typeof vif.name !== "string") {
                    throw new Error("VIF was not found");
                }
                this.search_results = [{
                    ...vif,
                    monitored: this.monitored_vifs.some(item => (
                        item.name === vif.name
                    )),
                }];
                this.selected_search_index = 0;
            })
            .catch(error => {
                this.search_message = request_error_message(
                    error,
                    `VIF '${name}' was not found.`
                );
            })
            .finally(() => {
                this.searching = false;
            });
        },

        add_selected_vif() {
            const vif = this.selected_search_vif;
            this.add_vif(vif);
        },

        add_vif_on_double_click(vif) {
            this.selected_search_index = this.search_results.indexOf(vif);
            this.add_vif(vif);
        },

        add_vif(vif) {
            if (!vif || vif.monitored || this.adding || this.removing) {
                return;
            }

            this.adding = true;
            this.message = "";
            api_client.run_post_query('/dashboard/vif_monitoring', {
                name: vif.name,
            })
            .then(response => {
                const monitored_vif = response.data && response.data.vif;
                if (!monitored_vif || typeof monitored_vif.name !== "string") {
                    throw new Error("Add operation returned an invalid response");
                }

                this.monitored_vifs.push(monitored_vif);
                this.monitored_vifs.sort((left, right) => (
                    left.name.localeCompare(right.name)
                ));
                vif.monitored = true;
                this.message_type = "success";
                this.message = `VIF '${vif.name}' was added to monitoring.`;
            })
            .catch(error => {
                this.message_type = "error";
                this.message = request_error_message(
                    error,
                    `Could not add VIF '${vif.name}'.`
                );
            })
            .finally(() => {
                this.adding = false;
            });
        },

        remove_selected_vif() {
            const vif = this.selected_monitored_vif;
            this.remove_vif(vif);
        },

        remove_vif_on_double_click(vif) {
            this.selected_monitored_index = this.monitored_vifs.indexOf(vif);
            this.remove_vif(vif);
        },

        remove_vif(vif) {
            if (!vif || this.adding || this.removing) {
                return;
            }

            this.removing = true;
            this.message = "";
            api_client.run_delete_query('/dashboard/vif_monitoring', {
                name: vif.name,
            })
            .then(() => {
                this.monitored_vifs = this.monitored_vifs.filter(item => (
                    item.name !== vif.name
                ));
                this.selected_monitored_index = null;
                if (this.search_results.length > 0 &&
                    this.search_results[0].name === vif.name) {
                    this.search_results[0].monitored = false;
                }
                this.message_type = "success";
                this.message = `VIF '${vif.name}' was removed from monitoring.`;
            })
            .catch(error => {
                this.message_type = "error";
                this.message = request_error_message(
                    error,
                    `Could not remove VIF '${vif.name}'.`
                );
            })
            .finally(() => {
                this.removing = false;
            });
        },
    },
};

</script>
